/**
 * Competitor API pricing — single source of truth.
 * All rates are per 1,000,000 tokens, in USD.
 *
 * Competitor prices are the fastest-staling data on this site. Update ONLY
 * here — every page that shows a competitor rate reads from this file.
 *
 * Verified 25 July 2026 against each provider's official pricing page.
 */

export const COMPETITOR_PRICING = {
  lastVerified: "2026-07-25",
  currency: "USD",
  perTokens: 1_000_000,

  models: {
    // --- OpenAI: GPT-5.6 family ---
    "gpt-5.6-sol": {
      label: "GPT-5.6 Sol",
      vendor: "OpenAI",
      input: 5.0,
      cachedInput: 0.5 as number | null,
      output: 30.0,
      note: null as string | null,
      longContext: { input: 10.0, cachedInput: 1.0, output: 45.0 },
      source: "https://openai.com/api/pricing/",
    },
    "gpt-5.6-terra": {
      label: "GPT-5.6 Terra",
      vendor: "OpenAI",
      input: 2.5,
      cachedInput: 0.25 as number | null,
      output: 15.0,
      note: null as string | null,
      longContext: { input: 5.0, cachedInput: 0.5, output: 22.5 },
      source: "https://openai.com/api/pricing/",
    },
    "gpt-5.6-luna": {
      label: "GPT-5.6 Luna",
      vendor: "OpenAI",
      input: 1.0,
      cachedInput: 0.1 as number | null,
      output: 6.0,
      note: null as string | null,
      longContext: { input: 2.0, cachedInput: 0.2, output: 9.0 },
      source: "https://openai.com/api/pricing/",
    },

    // --- OpenAI: GPT-5.5 / 5.4 family ---
    "gpt-5.5": {
      label: "GPT-5.5",
      vendor: "OpenAI",
      input: 5.0,
      cachedInput: 0.5 as number | null,
      output: 30.0,
      note: null as string | null,
      source: "https://openai.com/api/pricing/",
    },
    "gpt-5.4": {
      label: "GPT-5.4",
      vendor: "OpenAI",
      input: 2.5,
      cachedInput: 0.25 as number | null,
      output: 15.0,
      note: "Rates apply under 270K context.",
      source: "https://openai.com/api/pricing/",
    },
    "gpt-5.4-mini": {
      label: "GPT-5.4 mini",
      vendor: "OpenAI",
      input: 0.75,
      cachedInput: 0.075 as number | null,
      output: 4.5,
      note: null as string | null,
      source: "https://openai.com/api/pricing/",
    },
    "gpt-5.4-nano": {
      label: "GPT-5.4 nano",
      vendor: "OpenAI",
      input: 0.2,
      cachedInput: 0.02 as number | null,
      output: 1.25,
      note: null as string | null,
      source: "https://openai.com/api/pricing/",
    },

    // --- Anthropic ---
    "claude-haiku-4.5": {
      label: "Claude Haiku 4.5",
      vendor: "Anthropic",
      input: 1.0,
      cachedInput: 0.1 as number | null,
      output: 5.0,
      note: null as string | null,
      source: "https://www.anthropic.com/pricing",
    },
    "claude-sonnet-5": {
      label: "Claude Sonnet 5",
      vendor: "Anthropic",
      input: 2.0,
      cachedInput: 0.2 as number | null,
      output: 10.0,
      note: null as string | null,
      /** Announced rate change — do not present as current before `effective`. */
      priceChange: { effective: "2026-09-01", input: 3.0, cacheRead: 0.3, output: 15.0 },
      source: "https://www.anthropic.com/pricing",
    },
    "claude-opus-5": {
      label: "Claude Opus 5",
      vendor: "Anthropic",
      input: 5.0,
      cachedInput: 0.5 as number | null,
      output: 25.0,
      note: null as string | null,
      source: "https://www.anthropic.com/pricing",
    },
    "claude-fable-5": {
      label: "Claude Fable 5",
      vendor: "Anthropic",
      input: 10.0,
      cachedInput: 1.0 as number | null,
      output: 50.0,
      note: null as string | null,
      source: "https://www.anthropic.com/pricing",
    },
    "claude-opus-4.8": {
      label: "Claude Opus 4.8",
      vendor: "Anthropic",
      input: 5.0,
      cachedInput: 2.5 as number | null,
      output: 25.0,
      note: null as string | null,
      source: "https://www.anthropic.com/pricing",
    },

    // --- Google ---
    "gemini-3.1-pro": {
      label: "Gemini 3.1 Pro",
      vendor: "Google",
      input: 2.0,
      cachedInput: null as number | null,
      output: 12.0,
      note: "Prompts ≤200K tokens. Above 200K: $4.00 input / $18.00 output.",
      longContext: { threshold: 200_000, input: 4.0, output: 18.0 },
      source: "https://ai.google.dev/pricing",
    },
  },
} as const;

export type CompetitorKey = keyof typeof COMPETITOR_PRICING.models;

export const COMPETITOR_ORDER: CompetitorKey[] = [
  "gpt-5.5",
  "gpt-5.4",
  "claude-opus-4.8",
  "gemini-3.1-pro",
];

/**
 * ChatGPT consumer plans — US list prices.
 * Separate from the API rate card above; these are per-month, not per-token.
 */
export const CHATGPT_CONSUMER_PLANS = {
  lastVerified: "2026-07-25",
  currency: "USD",
  footnote: "US prices as of 25 July 2026; OpenAI localizes pricing in some markets.",
  plans: [
    {
      name: "Free",
      price: "$0",
      notes: "Limited GPT-5.5 Instant, limited messages/uploads/image generation",
    },
    {
      name: "Go",
      price: "$8/mo",
      notes:
        "10× more messages, uploads and image creation than Free; longer memory. OpenAI notes pricing is localized in some markets",
    },
    {
      name: "Plus",
      price: "$20/mo",
      notes: "GPT-5.6 reasoning models, projects, scheduled tasks, custom GPTs",
    },
    {
      name: "Pro",
      price: "$100/mo or $200/mo",
      notes: "Two separate tiers — the $200 tier runs GPT-5.5 Pro with 20× Plus limits",
    },
  ],
} as const;

/** How many times more expensive a competitor is than a given DeepSeek rate. */
export function multipleVs(competitorRate: number, deepseekRate: number): string {
  return (competitorRate / deepseekRate).toFixed(1) + "×";
}

/** How much cheaper DeepSeek is than a competitor, as a rounded percentage string. */
export function percentCheaperThan(competitorRate: number, deepseekRate: number): string {
  return Math.round((1 - deepseekRate / competitorRate) * 100) + "%";
}

/**
 * Render a per-1M-token rate. Two decimals when that is lossless
 * ($5.00, $0.14), full precision otherwise ($0.435, $0.0028).
 */
export function usd(rate: number): string {
  const two = rate.toFixed(2);
  return Number(two) === rate ? `$${two}` : `$${rate}`;
}

/**
 * Claude consumer/team plans — US list prices, per month.
 * Separate from the API rate card; these are subscriptions, not per-token.
 */
export const CLAUDE_CONSUMER_PLANS = {
  lastVerified: "2026-07-25",
  currency: "USD",
  plans: [
    { name: "Free", price: "$0" },
    { name: "Pro", price: "$20/mo, or $17/mo billed annually ($200 up front)" },
    { name: "Max", price: "from $100/mo — 5× or 20× Pro usage, higher output limits" },
    { name: "Team", price: "$20/seat/mo annually, $25 monthly; premium seats $100/$125" },
    { name: "Enterprise", price: "$20/seat plus usage" },
  ],
} as const;
